// Portfolio Data for ManikantaReddy Annem

export const personalInfo = {
  name: "ManikantaReddy Annem",
  headline: "Full-Stack Engineer building scalable systems, real-time platforms, and cloud-native products.",
  bio: "I design and ship reliable backend systems, event-driven architectures, and intelligent full-stack applications. I enjoy solving complex technical problems, working with distributed systems, and turning ambitious product ideas into production-ready software.",
  email: "manikanta.annem47@gmail.com",
  phone: "617-652-4672",
  linkedin: "https://www.linkedin.com/in/manikantaannem47/",
  github: "https://github.com/manikantareddy12",
  resumeUrl: "https://drive.google.com/uc?export=download&id=1_0B9pDuzfF8O1TclZbLxIL530BoDyswi"
};

export const stats = [
  { label: "Years of Experience", value: "5", subtitle: "Building production systems at scale" },
  { label: "Role", value: "Full-Stack Engineer" },
  { label: "Languages", value: "Java, Python, Kotlin, TypeScript, React, JavaScript" },
  { label: "Databases", value: "PostgreSQL, Redis, Oracle, MySQL, H2, MongoDB" },
  { label: "AI Tools", value: "ChatGPT, Claude, GitHub Copilot, Cursor, Postman AI, Docker Scout" },
  { label: "Achievement", value: "Built SaaS based Gaming POS", linkText: "ArcadeX", link: "https://arcadex.charviam.com/" }
];

export const floatingTags = [
  "Java", "Spring Boot", "Python", "Blockchain", "AWS", "Kafka", "React", "Microservices", "Docker", "PostgreSQL", "Redis"
];

export const aboutText = `I am a Full-Stack Engineer with 5 years of experience building scalable, cloud-native systems and real-time platforms that work reliably in production. I design and develop high-performance backends using Java, Spring Boot, and Python, and I regularly work with event-driven architectures powered by Kafka and RabbitMQ.

I prioritize security, performance, and maintainability in everything I build, using standards like JWT, OAuth2, and role-based access control to create systems that are both robust and compliant. I write clean, well-structured, and testable code because I believe great software is not just about what works today, but what can scale and evolve tomorrow.

I thrive in collaborative, agile environments where engineering, product, and design come together to solve real user problems. I take end-to-end ownership of systems from architecture and infrastructure to deployment, monitoring, and continuous improvement in production.`;

export const skills = {
  "Programming Languages": ["Java", "Python", "Kotlin", "TypeScript", "JavaScript", "Solidity", "C", "R", "Go", "Rust", "C++"],
  "Frontend & Web": ["React", "Angular", "React Native", "HTML", "CSS", "GraphQL", "Node.js"],
  "Backend & Frameworks": ["Spring Boot", "Spring Security", "Spring Data JPA", "Django REST", "Express", "JUnit"],
  "Blockchain & Web3": ["Ethereum", "ethers.js", "Hardhat", "OpenZeppelin", "R3 Corda", "Solidity"],
  "Databases & Storage": ["PostgreSQL", "MySQL", "Redis", "Apache Cassandra", "H2", "Liquibase", "Hibernate", "HikariCP"],
  "Cloud & DevOps": ["AWS (EC2, RDS, S3, CloudWatch)", "Docker", "Jenkins", "GitHub Actions", "NGINX", "Linux"],
  "Messaging & Integration": ["REST APIs", "Microservices", "Apache Kafka", "RabbitMQ", "WebSockets", "JWT", "OAuth2"]
};

export const experiences = [
  {
    company: "Peaks Digital Marketing",
    role: "Full-Stack AI Developer",
    period: "April 2026 – Present",
    location: "Denver, CO",
    description: [
      "Led end-to-end development of a full-stack web platform supporting SEO and digital marketing workflows - spanning user onboarding, internal operations, and client delivery using React frontends and Python backend services.",
      "Designed and deployed agentic AI-driven tools and automation workflows to streamline SEO strategies, content pipelines, and cross-departmental processes, reducing manual effort and improving operational efficiency.",
      "Architected scalable backend systems in Python and built responsive, high-performance frontend interfaces delivering a seamless user experience across internal teams and clients.",
      "Engineered cloud-based solutions on Google Cloud, including REST API integrations, data pipelines, and service orchestration with secure API key and credential management.",
      "Built a centralized platform with multi-level role-based access control (organization, internal teams, and clients), enabling streamlined and secure workflow management.",
      "Managed the complete application lifecycle, system design, development, deployment, and performance optimization while integrating CMS platforms (WordPress, Shopify) and third-party SEO tools (SEMrush, Ahrefs)."
    ]
  },
  {
    company: "Ascent",
    role: "Senior Software Engineer",
    period: "July 2025 – April 2026",
    location: "Remote",
    description: [
      "Designed secure, scalable Java and Spring Boot microservices orchestrating loan lifecycle workflows and integrating Ethereum services for on-chain registration.",
      "Optimized financial modules with Redis caching, transactional logic, and SQL tuning to synchronize off-chain data with Ethereum states.",
      "Built Kafka event-driven microservices handling blockchain events like loan approvals, repayments, and transaction hash propagation.",
      "Engineered resilient service-layer components for transaction state management, idempotency handling, and on-chain reconciliation logic.",
      "Integrated Angular dashboards with REST APIs and WebSocket streams exposing blockchain statuses, gas fees, and confirmation events.",
      "Strengthened security with RBAC, OAuth2/JWT, encrypted APIs, and structured logging to protect borrower and wallet metadata.",
      "Built CI/CD pipelines using Jenkins and Docker to automate deployment of blockchain-integrated microservices on AWS infrastructure."
    ]
  },
  {
    company: "Charviam — ArcadeX SaaS Platform",
    role: "Independent Full-Stack Developer",
    period: "Nov 2025 – Feb",
    location: "Remote",
    description: [
      "Built a multi-tenant SaaS gaming POS platform enabling touchless customer identification, ticketing, rewards, and multi-shop analytics.",
      "Designed and implemented a full-stack architecture using Django REST, React, Kotlin, PostgreSQL, and Redis with scalable APIs.",
      "Integrated palm-vein biometrics, NFC identification, and secure role-based access control across all services.",
      "Developed a React Native Android kiosk app with Kotlin hardware integration for biometric scanning and thermal ticket printing.",
      "Deployed and operated production systems on AWS using EC2, RDS, Docker, Redis, and NGINX with high availability."
    ],
    links: ["https://charviam.com/", "https://arcadex.charviam.com/"]
  },
  {
    company: "E.C Studios / Everyday Chemist",
    role: "Software Engineer Apprentice",
    period: "Jan 2025 – May 2025",
    location: "Remote",
    description: [
      "Developed and maintained Spring Boot–based internal tools for cosmetic product planning and vendor tracking with reliable enhancements and fixes.",
      "Refined frontend interfaces to clearly surface project milestones, formulation progress, and research documentation for cross-team visibility.",
      "Integrated email notification and document storage services to automate workflows and reduce coordination friction across teams.",
      "Performed data cleanup, wrote SQL queries, updated schemas, and supported code reviews and deployment checklists for stable releases."
    ]
  },
  {
    company: "Tata Consultancy Services",
    role: "Systems Engineer",
    period: "Aug 2021 – Aug 2023",
    location: "Hyderabad, India",
    description: [
      "Built secure, scalable blockchain solutions for Vodafone using Java, Kotlin, and R3 Corda with reliable smart contracts.",
      "Architected and delivered 20+ high-performance REST microservices for ledger synchronization and contract execution.",
      "Implemented RabbitMQ with Spring AMQP to enable durable, parallel, and asynchronous event-driven workflows.",
      "Developed optimized Spring Data JPA and Hibernate modules to improve throughput and reduce distributed bottlenecks.",
      "Engineered Docker- and Jenkins-based CI/CD pipelines that significantly reduced deployment time with zero-downtime rollouts.",
      "Secured APIs with Spring Security, JWT, OAuth2, RBAC, and structured SLF4J/Log4j logging for observability.",
      "Managed Liquibase migrations for PostgreSQL and H2 to ensure consistent, reliable schema changes across environments.",
      "Prepared AWS release scripts, handled env configs, database migrations, and monitored systems via CloudWatch."
    ]
  },
  {
    company: "FuGenX",
    role: "Software Engineer Intern",
    period: "July 2020 – July 2021",
    location: "Hyderabad, India",
    description: [
      "Built and maintained enterprise-grade Spring Boot backends with secure OAuth2/JWT authentication and RBAC controls.",
      "Designed Kafka-based event-driven microservices that improved throughput and reduced messaging bottlenecks in production.",
      "Developed and documented REST APIs with Swagger to streamline cross-team integration and testing workflows.",
      "Optimized PostgreSQL performance using JPA/Hibernate tuning, HikariCP pooling, and well-structured SQL queries.",
      "Integrated backend services with Angular applications using WebSockets to deliver real-time data updates."
    ]

  },
  {
    company: "Amphisoft",
    role: "Backend Developer Intern",
    period: "Nov 2019 – June 2020",
    location: "Coimbatore, India",
    description: [
      "Developed and enhanced Java-based backend features for an online learning platform using Spring-style service patterns.",
      "Wrote and optimized SQL queries to reliably store and retrieve course, user, and activity data.",
      "Debugged and refactored Java modules using object-oriented design principles to improve code clarity and stability.",
      "Supported API testing with Postman, documented results, and assisted in integrating backend updates into workflows."
    ]

  }
];

export const dabProject = {
  title: "Digital Asset Broker Platform",
  overview: "An enterprise-grade Digital Asset Broker and loan management platform designed to bridge traditional financial services with Ethereum blockchain, replacing siloed record-keeping with a tamper-proof, auditable hybrid architecture.\n\nThe platform enables secure loan lifecycle management through a four-tier microservices system with React Admin UI, Spring Boot Loan Service, Node.js Blockchain Gateway, and Solidity Smart Contracts with role-based access control spanning the UI through to EVM-enforced on-chain roles.\n\nBy implementing a hybrid on-chain/off-chain design where business logic lives in PostgreSQL and only approved loans and repayments are immutably registered on Ethereum, the platform balances cost-efficiency with cryptographic data integrity, reconciliation, and enterprise-grade audit trails via Kafka event streaming.",
  highlights: [
    "React admin dashboard with real-time loan analytics and role-gated actions.",
    "Spring Boot loan service managing full lifecycle with blockchain registration on approval.",
    "Node.js blockchain gateway handling transaction signing, gas estimation, and retry logic.",
    "Two Solidity smart contracts for immutable loan registry and repayment ledger.",
    "Containerized deployment with Docker Compose orchestrating six interdependent services."
  ],
  technical: [
    "Blockchain integration via ethers.js v6 with idempotency-keyed transaction management.",
    "OpenZeppelin AccessControl, Pausable, and ReentrancyGuard enforcing on-chain security roles.",
    "Kafka-driven event pipeline with scheduled reconciliation detecting on-chain/off-chain mismatches.",
    "Hardhat local blockchain network used to deploy contracts and execute real transactions.",
    "Flyway-managed database migrations with normalized loan, repayment, and audit schemas."
  ],
  links: [
    { label: "digital-asset-broker", url: "https://github.com/manikantareddy12/digital-asset-broker-platform" }
  ],
  techStack: ["Java", "Spring", "Flyway", "Node.js", "Express", "ethers.js", "Solidity", "OpenZeppelin", "Hardhat", "React", "TypeScript", "PostgreSQL", "Apache Kafka", "Docker", "GitHub Actions"]
};

export const featuredProject = {
  title: "ArcadeX — Digital Arcade POS Platform for Gaming Centers",
  overview: "A digital transformation platform designed to modernize arcade and entertainment businesses by replacing manual, fragmented processes with an intelligent, integrated POS and operations system.\n\nThe platform enables touchless customer identification through palm-vein biometrics, seamless ticket and reward management, real-time analytics across multiple locations, and streamlined employee scheduling.\n\nBy unifying customer experience, workforce management, and business intelligence into a single system, ArcadeX delivers a secure, scalable, and data-driven foundation for next-generation arcade operations.",
  highlights: [
    "Django REST backend with 18 specialized modules including authentication, biometrics, device control, and analytics.",
    "React web portal for business owners and employees to manage operations.",
    "React Native Android kiosk app for customer interactions, palm scanning, NFC, and thermal ticket printing.",
    "Kotlin-based hardware bridge application for palm-vein scanner integration.",
    "Multi-tenant architecture with strict data isolation and role-based access control."
  ],
  technical: [
    "Palm-vein biometric authentication using RGB and IR feature vectors.",
    "Secure kiosk activation using employee PINs and device-specific JWT tokens.",
    "Ticket and reward management with barcode-based redemption.",
    "Workforce management with timesheets and shift scheduling.",
    "AWS deployment using EC2, RDS, Redis, Docker, and NGINX."
  ],
  links: [
    { label: "Charviam Website", url: "https://charviam.com/" },
    { label: "ArcadeX Platform", url: "https://arcadex.charviam.com/" }
  ],
  techStack: ["Python", "Django REST", "React", "React Native", "Kotlin", "PostgreSQL", "Redis", "AWS", "Docker", "NGINX"]
};

export const featuredProjects = [dabProject, featuredProject];

export const education = [
  {
    institution: "Fitchburg State University",
    location: "MA",
    degree: "Master of Science in Computer Science",
    gpa: "3.81",
    period: "Aug 2023 – May 2025",
    coursework: ["Operating Systems", "Networking", "Machine Learning", "Secure Distributed Systems", "Data Mining", "Software Engineering"]
  },
  {
    institution: "QIS College of Engineering & Technology",
    location: "India",
    degree: "Bachelor of Technology in Electronics and Communication",
    gpa: "3.77",
    period: "",
    coursework: ["Data Structures Algorithms", "Communications", "Computer Architecture", "Databases", "Web Development", "VLSI Design"]
  }
];

export const certifications = [
  { name: "Google AI Essentials", issuer: "Google · Coursera", url: "https://coursera.org/share/bc90b975388e47fdd81b3c5623fa0bc7" },
  { name: "Java Spring Framework, Spring Boot, Spring AI", issuer: "Udemy", url: "https://www.udemy.com/certificate/UC-72c13943-093f-4027-a7b6-3d5cbff555cb/" },
  { name: "Spring 6 with Spring Boot 3", issuer: "LinkedIn", url: "https://www.linkedin.com/learning/certificates/985d6f1325a38258212e347555f535e04a170af3545e29d3e648e1b375e0d54c?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_certifications_details%3BI6vIzelKQ%2FelSbjyimSSWQ%3D%3D" },
  { name: "Node.js Essential Training", issuer: "LinkedIn", url: "https://www.linkedin.com/learning/certificates/30e3f5af1055f6650f518f5cb589f235672652dd0c8b8408fbf0e3b43ee5eb92?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_certifications_details%3BI6vIzelKQ%2FelSbjyimSSWQ%3D%3D" },
  { name: "Corda Certified Developer", issuer: "R3", url: "https://www.credly.com/badges/e846226e-0779-429b-aa6e-85ae12a5cd0e?source=linked_in_profile" },
  { name: "Cyber Crime and Security", issuer: "Indian Servers" },
  { name: "Data Structures Certification", issuer: "ABC Technologies" }
];

export const techIcons = [
  "Java", "Python", "Spring Boot", "Django", "React", "React Native",
  "AWS", "Docker", "Kafka", "PostgreSQL", "Redis", "Jenkins",
  "Git", "Linux", "IntelliJ", "VS Code"
];

export const navItems = [
  { label: "About", href: "#about" },
  { label: "Skills", href: "#skills" },
  { label: "Experience", href: "#experience" },
  { label: "Projects", href: "#projects" },
  { label: "Education", href: "#education" },
  { label: "Contact", href: "#contact" }
];

export const githubProjects = [
  {
    title: "RESTful API Development",
    description: "Built a high-performance REST API for job postings using Spring Boot, Java 21, and Maven, with robust CRUD operations and layered architecture. Delivered scalable backend design and modern API development.",
    techStack: ["Java 21", "Spring Boot", "Maven", "REST API"]
  },
  {
    title: "Hibernate With PostgreSQL",
    description: "Implemented ORM with advanced mappings, caching, and schema customization in Java 21 and PostgreSQL. Achieved scalable, maintainable persistence for complex entity relationships.",
    techStack: ["Java 21", "Hibernate", "PostgreSQL", "ORM"]
  },
  {
    title: "Spring Boot Laptop Management System",
    description: "Developed a backend management system using Spring Boot and a Service–Repository layered architecture. Implemented structured CRUD operations for device inventory, integrated Spring Security for role-based access control (RBAC) and leveraged dependency injection principles. Deployed via Docker.",
    techStack: ["Spring Boot", "Spring Security", "Docker", "RBAC"]
  },
  {
    title: "McDonald's Customer Sentiment Analysis",
    description: "Led an NLP-driven sentiment analysis project on 33,000+ customer reviews using Python, SVM, and text classification techniques. Achieved 99.17% sentiment prediction accuracy using F1 Score and MSE as validation metrics.",
    techStack: ["Python", "NLP", "SVM", "Machine Learning"]
  },
  {
    title: "Restaurant Billing System",
    description: "Developed a robust restaurant billing system using Java, leveraging object-oriented principles like inheritance, polymorphism, and encapsulation to create a modular, reusable, and scalable solution with efficient order tracking and chef allocation.",
    techStack: ["Java", "OOP", "Design Patterns"]
  },
  {
    title: "Library Management System",
    description: "Built a user-friendly library administration system with Tkinter for the GUI and SQLite for database management, streamlining book management, user authentication, and real-time transactions with quick CRUD operations.",
    techStack: ["Python", "Tkinter", "SQLite", "GUI"]
  }
];
